import { useEffect, useMemo, useState } from 'react'
import { ChevronDown, Plus, Search, X } from 'lucide-react'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog'
import { Input } from '../../../components/ui/input'
import { Button } from '../../../components/ui/button'
import { Badge } from '../../../components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select'
import { ScrollArea } from '../../../components/ui/scroll-area'
import type {
  Classroom,
  ClassroomStatus,
  CreateClassroomPayload,
  StarterPrompt,
  StudentItem,
  UpdateClassroomPayload,
} from '../../../api/backend'

export function formatShortDate(value?: string | null) {
  if (!value) return '—'
  return new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

export function formatClassroomStatusLabel(status: ClassroomStatus | string) {
  switch (status) {
    case 'PREPARING':
      return 'Preparing'
    case 'IN_PROGRESS':
      return 'In Progress'
    case 'ENDED':
      return 'Ended'
    default:
      return status
    }
}

export function StatusBadge({ status }: { status: ClassroomStatus | string }) {
  const variant =
    status === 'IN_PROGRESS'
      ? 'success'
      : status === 'ENDED'
        ? 'attention'
        : 'muted'

  return (
    <Badge variant={variant}>
      {formatClassroomStatusLabel(status)}
    </Badge>
  )
}

interface ClassroomDialogProps {
  classroom?: Classroom | null
  open: boolean
  onClose: () => void
  teachers?: StudentItem[]
  onSubmit: (payload: (CreateClassroomPayload & UpdateClassroomPayload) & {
    teacherUserIds: string[]
  }) => Promise<void> | void
}

const TOPIC_LIMIT = 5
const TOPIC_CHAR_LIMIT = 20

function parseStarterPrompts(raw: string): StarterPrompt[] | null {
  const lines = raw
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)

  if (lines.length === 0) return null

  return lines.map((line) => ({
    label: line.length > 32 ? `${line.slice(0, 29)}...` : line,
    prompt: line,
  }))
}

export function ClassroomDialog({
  classroom,
  open,
  onClose,
  onSubmit,
  teachers = [],
}: ClassroomDialogProps) {
  const isCreateMode = !classroom
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [topicInput, setTopicInput] = useState('')
  const [topics, setTopics] = useState<string[]>([])
  const [teacherId, setTeacherId] = useState<string>('unassigned')
  const [starterPromptsInput, setStarterPromptsInput] = useState('')
  const [showStarterPrompts, setShowStarterPrompts] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const starterPromptsCount = useMemo(
    () => parseStarterPrompts(starterPromptsInput)?.length ?? 0,
    [starterPromptsInput],
  )

  useEffect(() => {
    if (!open) return
    setTitle(classroom?.title ?? '')
    setDescription(classroom?.description ?? '')
    setTopicInput('')
    setTopics(classroom?.topics ?? [])
    setTeacherId(classroom?.primaryTeacherId ?? 'unassigned')
    setStarterPromptsInput(classroom?.starterPrompts?.map((item) => item.prompt).join('\n') ?? '')
    setShowStarterPrompts(false)
  }, [classroom, open])

  const handleAddTopic = () => {
    if (topics.length >= TOPIC_LIMIT) return
    const next = topicInput.trim().slice(0, TOPIC_CHAR_LIMIT)
    if (!next) return
    if (topics.includes(next)) {
      setTopicInput('')
      return
    }
    setTopics((prev) => [...prev, next])
    setTopicInput('')
  }

  const handleSubmit = async () => {
    if (!title.trim()) return
    setIsSaving(true)
    try {
      await onSubmit({
        title: title.trim(),
        description: isCreateMode ? undefined : description.trim() || undefined,
        topics,
        starterPrompts: isCreateMode ? undefined : parseStarterPrompts(starterPromptsInput),
        materials: isCreateMode ? undefined : classroom?.materials ?? undefined,
        teacherUserIds: isCreateMode ? [] : teacherId !== 'unassigned' ? [teacherId] : [],
      })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-xl" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{classroom ? 'Edit Class' : 'Create Class'}</DialogTitle>
        </DialogHeader>

        <div className="grid gap-4">
          <div className="grid gap-1.5">
            <label className="text-sm font-heading text-foreground">
              Class Name
            </label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Science Period 3" />
          </div>

          <div className="grid gap-1.5">
            <label className="text-sm font-heading text-foreground">
              Topics
            </label>
            <div className="flex items-start gap-2">
              <Input
                value={topicInput}
                onChange={(e) => setTopicInput(e.target.value.slice(0, TOPIC_CHAR_LIMIT))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleAddTopic()
                  }
                }}
                placeholder="e.g. Volcanoes"
                className="flex-1"
                maxLength={TOPIC_CHAR_LIMIT}
              />
              <Button
                type="button"
                variant="neutral"
                onClick={handleAddTopic}
                className="shrink-0"
                disabled={!topicInput.trim() || topics.length >= TOPIC_LIMIT}
              >
                <Plus className="size-4" />
                Add
              </Button>
            </div>
            <div className="flex items-center justify-between gap-2 text-xs text-foreground/45">
              <p>Optional, up to {TOPIC_LIMIT} topics</p>
              <p>{topicInput.length}/{TOPIC_CHAR_LIMIT}</p>
            </div>
            {topics.length > 0 ? (
              <div className="flex flex-wrap gap-2 pt-1">
                {topics.map((topic) => (
                  <Badge
                    key={topic}
                    variant="neutral"
                    className="flex max-w-full items-center gap-1 px-2 py-1"
                  >
                    <span className="min-w-0 max-w-[14rem] truncate">
                      {topic}
                    </span>
                    <button
                      type="button"
                      className="shrink-0"
                      onClick={() => setTopics((prev) => prev.filter((item) => item !== topic))}
                    >
                      <X className="size-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            ) : null}
          </div>

          {!isCreateMode ? (
            <div className="grid gap-1.5">
              <label className="text-sm font-heading text-foreground">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="min-h-24 rounded-base border-2 border-nb-border bg-background px-3 py-2 text-sm outline-none focus:border-main"
                placeholder="Brief description..."
              />
            </div>
          ) : null}

          {!isCreateMode ? (
            <div className="grid gap-1.5">
              <label className="text-sm font-heading text-foreground">
                Teacher
              </label>
              <Select value={teacherId} onValueChange={setTeacherId}>
                <SelectTrigger>
                  <SelectValue placeholder="Unassigned" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {teachers.map((teacher) => (
                    <SelectItem key={teacher.userId} value={teacher.userId}>
                      {teacher.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}

          {!isCreateMode ? (
            <div className="grid gap-2">
              <button
                type="button"
                className="flex items-center justify-between rounded-base border-2 border-nb-border bg-background px-3 py-2 text-left"
                onClick={() => setShowStarterPrompts((prev) => !prev)}
              >
                <span className="text-sm font-heading text-foreground">
                  Starter Prompts
                  {starterPromptsCount > 0 ? ` (${starterPromptsCount})` : ''}
                </span>
                <ChevronDown className={`size-4 transition-transform ${showStarterPrompts ? 'rotate-180' : ''}`} />
              </button>
              {showStarterPrompts && (
                <textarea
                  value={starterPromptsInput}
                  onChange={(e) => setStarterPromptsInput(e.target.value)}
                  rows={5}
                  className="min-h-28 rounded-base border-2 border-nb-border bg-background px-3 py-2 text-sm outline-none focus:border-main"
                  placeholder="One starter prompt per line"
                />
              )}
            </div>
          ) : null}
        </div>

        <p className="text-xs leading-5 text-foreground/55">
          Create the container first. Add teacher, schedule, roster, materials, and join details from the class page.
        </p>

        <DialogFooter>
          <Button size="sm" onClick={handleSubmit} disabled={isSaving || !title.trim()}>
            {isSaving ? 'Saving…' : classroom ? 'Save Class' : 'Create Class'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
